package com.klantenstop.web.pages.admin.moderation

import com.klantenstop.web.data.models.domain.toModerationEntry
import com.klantenstop.web.data.models.dto.responses.moderation.ModerationEntityType
import com.klantenstop.web.data.repos.ModerationRepo
import com.materialkobweb.utils.ViewModel
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ModerationQueueViewModel(
    private val entityType: ModerationEntityType?,
    private val moderationRepo: ModerationRepo = ModerationRepo,
) : ViewModel(), ModerationQueueContract {

    private val _state = MutableStateFlow(ModerationQueueContract.State())
    override val state: StateFlow<ModerationQueueContract.State> = _state.asStateFlow()

    private val _effect = MutableSharedFlow<ModerationQueueContract.Effect>()
    override val effect: SharedFlow<ModerationQueueContract.Effect> = _effect.asSharedFlow()

    override fun sendIntent(intent: ModerationQueueContract.Intent) = coroutineScope.launch {
        when (intent) {
            is ModerationQueueContract.Intent.LoadQueue -> loadQueue(intent.entityType, intent.page)
            is ModerationQueueContract.Intent.ChangePage -> loadQueue(entityType, intent.newPage)
            is ModerationQueueContract.Intent.ToggleRow -> toggleRow(intent.entryId)
            is ModerationQueueContract.Intent.ApproveEntry -> approveEntry(intent.entryId)
            is ModerationQueueContract.Intent.ShowRejectDialog -> showRejectDialog(intent.entryId)
            is ModerationQueueContract.Intent.HideRejectDialog -> hideRejectDialog()
            is ModerationQueueContract.Intent.RejectEntry -> rejectEntry(intent.entryId, intent.reason)
        }
    }

    override fun emitEffect(effect: ModerationQueueContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (ModerationQueueContract.State) -> ModerationQueueContract.State) {
        _state.update(block)
    }

    private suspend fun loadQueue(entityType: ModerationEntityType?, page: Int) {
        updateState { it.copy(isLoading = true, error = null, page = page) }
        try {
            val queueDto = when (entityType) {
                ModerationEntityType.COMPANY -> moderationRepo.getCompanyQueue(page = page, pageSize = 20)
                ModerationEntityType.STATUS -> moderationRepo.getStatusQueue(page = page, pageSize = 20)
                ModerationEntityType.REVIEW -> moderationRepo.getReviewQueue(page = page, pageSize = 20)
                else -> throw IllegalArgumentException("Unknown entity type: $entityType")
            }

            updateState {
                it.copy(
                    items = queueDto.items.map { dto -> dto.toModerationEntry() },
                    total = queueDto.total,
                    isLoading = false
                )
            }
        } catch (e: Exception) {
            updateState { it.copy(isLoading = false, error = e.message) }
            emitEffect(ModerationQueueContract.Effect.ShowError(e.message ?: "Fout bij laden van wachtrij"))
        }
    }

    private suspend fun toggleRow(entryId: String) {
        val currentExpandedId = _state.value.expandedRowId

        if (currentExpandedId == entryId) {
            // Collapse the row
            updateState { it.copy(expandedRowId = null) }
        } else {
            // Expand the row and clear any previous error for this entry
            updateState { state ->
                state.copy(
                    expandedRowId = entryId,
                    snapshotErrorByEntryId = state.snapshotErrorByEntryId - entryId
                )
            }

            // Check if entry detail is already cached
            if (_state.value.entryDetailCache[entryId] == null) {
                // Load entry detail
                loadEntryDetail(entryId)
            }
        }
    }

    private suspend fun loadEntryDetail(entryId: String) {
        // Mark this entry as loading
        updateState { state ->
            state.copy(
                loadingEntryIds = state.loadingEntryIds + entryId,
                snapshotErrorByEntryId = state.snapshotErrorByEntryId - entryId
            )
        }
        
        try {
            val entryDetail = moderationRepo.getEntryDetail(entryId)

            updateState { state ->
                state.copy(
                    loadingEntryIds = state.loadingEntryIds - entryId,
                    entryDetailCache = state.entryDetailCache + (entryId to entryDetail)
                )
            }
        } catch (e: Exception) {
            updateState { state ->
                state.copy(
                    loadingEntryIds = state.loadingEntryIds - entryId,
                    snapshotErrorByEntryId = state.snapshotErrorByEntryId + (entryId to (e.message ?: "Fout bij laden van entry detail"))
                )
            }
        }
    }

    private suspend fun approveEntry(entryId: String) {
        try {
            moderationRepo.approveEntry(entryId)
            emitEffect(ModerationQueueContract.Effect.ShowSuccess("Entry goedgekeurd"))
            // Reload the queue
            loadQueue(entityType, _state.value.page)
        } catch (e: Exception) {
            emitEffect(ModerationQueueContract.Effect.ShowError(e.message ?: "Fout bij goedkeuren entry"))
        }
    }

    private suspend fun rejectEntry(entryId: String, reason: String) {
        try {
            moderationRepo.rejectEntry(entryId, reason)
            emitEffect(ModerationQueueContract.Effect.ShowSuccess("Entry afgekeurd"))
            // Hide dialog and reload the queue
            hideRejectDialog()
            loadQueue(entityType, _state.value.page)
        } catch (e: Exception) {
            emitEffect(ModerationQueueContract.Effect.ShowError(e.message ?: "Fout bij afkeuren entry"))
        }
    }

    private fun showRejectDialog(entryId: String) {
        updateState { it.copy(rejectDialogEntryId = entryId) }
    }

    private fun hideRejectDialog() {
        updateState { it.copy(rejectDialogEntryId = null) }
    }
}
